import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import { ColumnSettings } from '@common/interfaces/column-settings.interface';
import { GridSettings } from '@common/interfaces/grid-settings.interface';
import { resolveProperty } from '@common/models/util';
import { ValueOf } from '@common/models/util.interface';
import { ViewMode } from '@common/models/view-mode';
import { CommonService } from '@common/services/common.service';
import { environment } from '@environments/environment';
import { tapSuccessResult } from '@ngneat/query';
import {
    CellClickEvent,
    CellCloseEvent,
    ColumnBase,
    ColumnComponent,
    DataStateChangeEvent,
    DetailExpandEvent,
    DetailTemplateDirective,
    GridComponent,
    GridDataResult,
    GridSize,
    PageChangeEvent,
    PagerSettings,
    RowArgs,
    RowClassArgs,
    RowClassFn,
    ScrollMode,
    SelectableSettings,
    SortSettings,
    ToolbarTemplateDirective
} from '@progress/kendo-angular-grid';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { SortDescriptor, State, orderBy, process } from '@progress/kendo-data-query';
import _ from 'lodash';
import { Subject, take, takeUntil } from 'rxjs';
import { ActionBarGroup } from '../action-bar/action-bar.interface';

/**
 * Our grid wrapper
 *
 * You can project items into the toolbar like so:
 * ```html
 * <app-grid ...>
 *   <kendo-dropdownlist toolbar/>
 * </app-grid>
 * ```
 */
@Component({
    selector: 'app-grid',
    templateUrl: './app-grid.component.html',
    styleUrls: ['./app-grid.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AppGridComponent implements OnInit, OnDestroy, OnChanges, AfterContentInit, AfterViewInit {
    private destroy$ = new Subject<boolean>();
    public id = _.uniqueId('app-grid-');
    gridData: GridDataResult = { data: [], total: 0 };
    state: State = { skip: 0 };
    _cols: ColumnSettings[] = [];

    @ContentChild(ToolbarTemplateDirective) protected toolbar: ToolbarTemplateDirective;
    @ContentChild(ToolbarTemplateDirective) protected toolbarCenter: ToolbarTemplateDirective;
    @ContentChild(DetailTemplateDirective) public detailTemplate: DetailTemplateDirective;
    @ContentChildren(ColumnBase) public columns = new QueryList<ColumnBase>();

    @ViewChild(GridComponent) public grid: GridComponent;
    @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

    @Input({ required: true }) data: GridDataResult | any[] | any;
    @Input() buttons: ActionBarGroup[];
    @Input() filterable = environment.settings.grid.filterable;
    @Input() reorderable = environment.settings.grid.reorderable;
    @Input() resizable = environment.settings.grid.resizable;
    @Input() autoFitColumns = environment.settings.grid.autoFitColumns && this.resizable;
    @Input() sortable = environment.settings.grid.sortable as SortSettings;
    @Input() sort = [...environment.settings.grid.sort] as SortDescriptor[];
    @Input() customSort: { field: string; sortFn: (row: any) => any };
    @Input() isLocalSortDisabled: boolean;
    @Input() forceLocalSort: boolean = false;
    @Input() selectable: boolean | SelectableSettings;
    @Input() scrollable: ScrollMode = 'scrollable';
    @Input() gridHeight: number;
    @Input() toolbarTitle: string;
    @Input() hideHeader: boolean = false;
    @Input() showToolbar: boolean = true;
    @Input() showNativeGrid: boolean = true;
    @Input() selectBy: 'row' | string = 'id';
    @Input() gridSize: GridSize = 'medium';
    @Input() resizeToWindow = false;
    @Input() pageSize: number = environment.settings.grid.pageSize;
    @Input() selection = [];
    @Input() pageable: PagerSettings | boolean = environment.settings.grid.pageable as PagerSettings;
    @Input() isBusy: boolean;
    @Input() rowClass: RowClassFn = (context: RowClassArgs) => null;
    @Input() nrOfActiveFilters: number;
    @Input() set cols(value: ColumnSettings[]) {
        this._cols = value.map((col, index) => ({
            type: 'string',
            isVisible: true,
            isSortable: true,
            width: col.width === undefined ? (index === value.length - 1 ? null : 100) : col.width,
            ...col
        }));
    }
    get cols(): ColumnSettings[] {
        return this._cols;
    }
    @Input() parentRoute: string;
    @Input() defaultViewMode: ValueOf<typeof ViewMode> = ViewMode.view;
    @Input() linkUrlOverride: string = null;
    @Input() queryData: (grid?: GridComponent) => void = () => null;
    @Input() allowGridSave: boolean = true;
    @Input() applyLocalPagination: boolean;

    @Output() selectedKeysChange = new EventEmitter<any[]>();
    @Output() pageChange = new EventEmitter<PageChangeEvent>();
    @Output() sortChange = new EventEmitter<SortDescriptor[]>();
    @Output() cellClick = new EventEmitter<CellClickEvent>();
    @Output() cellClose = new EventEmitter<CellCloseEvent>();
    @Output() toggleFilter = new EventEmitter();
    @Output() clearFilter = new EventEmitter();
    @Output() saveGridSettings = new EventEmitter();
    @Output() deleteGridSettings = new EventEmitter();

    @HostListener('window:resize')
    onResize(): void {
        this.calculateGridHeight();
    }

    public selectionKey(context: RowArgs): string {
        return context.dataItem.id;
    }

    public gridSettings: GridSettings;
    public gridSettingKey: string;
    public resolveProperty = resolveProperty;
    saveAndLoadButtonsVisible = false;
    gridSettingsExist = false;
    private _savedStateExists: boolean;

    isGridCompressed = false;
    compressedGridValue = environment.settings.grid.compressedGridValue;
    hasContentProjection = false;

    private originalData: any[] = [];
    private originalColumnState = new Map<string, { width: number; orderIndex: number; hidden: boolean }>();

    public get savedStateExists(): boolean {
        return this._savedStateExists;
    }

    public set savedStateExists(value: boolean) {
        this._savedStateExists = value;
    }

    public get isSelectable(): boolean {
        return (
            (typeof this.selectable === 'boolean' && this.selectable) ||
            (this.selectable as SelectableSettings)?.enabled
        );
    }

    public get showSelectAll(): boolean {
        const selectableSettings = this.selectable as SelectableSettings;
        return (
            (typeof this.selectable === 'boolean' && this.selectable) ||
            (selectableSettings.enabled && selectableSettings.mode !== 'single')
        );
    }

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        protected commonService: CommonService,
        protected router: Router,
        private ngZone: NgZone
    ) {}

    ngOnInit() {
        if (this.allowGridSave) {
            this.getSavedState();
            this.loadGridSettings();
        }
        this.state.take = this.gridSettings?.state?.take || this.pageSize;
        this.state.sort = this.gridSettings?.state?.sort || this.sort;
    }

    ngAfterContentInit() {
        this.hasContentProjection = this.columns.some(
            (col) => col instanceof ColumnComponent && !!(col as ColumnComponent).field
        );
    }

    ngAfterViewInit() {
        this.grid.toolbarTemplate = this.toolbar;
        this.grid.detailTemplate = this.detailTemplate;
        if (this.gridSettings.columnsConfig.length > 0) {
            if (this.hasContentProjection) {
                this.applyColumnConfigToContentChildren(this.gridSettings.columnsConfig);
            } else {
                this.columns = this.grid.columns;
            }
        }
        this.calculateGridHeight();
        this.updateColumns();
        this.columns.changes.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateColumns());
        if (this.selectBy !== 'id') this.changeSelection();
        if (this.autoFitColumns) setTimeout(() => this.fitColumns(), 1000);
    }

    private applyColumnConfigToContentChildren(columnsConfig: ColumnSettings[]): void {
        const contentCols = this.columns.toArray() as ColumnComponent[];
        contentCols.forEach((col) => {
            if (!col.field) return;
            if (!this.originalColumnState.has(col.field)) {
                this.originalColumnState.set(col.field, {
                    width: col.width,
                    orderIndex: col.orderIndex,
                    hidden: col.hidden ?? false
                });
            }
            const saved = columnsConfig.find((s) => s.field === col.field);
            if (saved) {
                col.width = saved.width;
                col.orderIndex = saved.orderIndex;
                col.hidden = saved.isHidden ?? false;
            }
        });
        const sorted = [...contentCols].sort((a, b) => {
            const ai = columnsConfig.find((s) => s.field === a.field)?.orderIndex ?? 9999;
            const bi = columnsConfig.find((s) => s.field === b.field)?.orderIndex ?? 9999;
            return ai - bi;
        });
        this.columns.reset(sorted);
    }

    ngOnDestroy() {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.isBusy) this.isBusy = changes.isBusy.currentValue;

        if (!changes.data) return;

        if (changes.sort) this.state.sort = this.sort;

        if (changes.data?.currentValue) {
            const currentValue = changes.data.currentValue;
            if (Array.isArray(currentValue)) {
                this.gridData = { data: currentValue, total: currentValue.length };
                this.processGridData(this.gridData.data, this.state);
            } else if ('total' in currentValue && 'data' in currentValue) {
                this.gridData = { data: currentValue.data, total: currentValue.total };
            } else if (currentValue?.isSuccess) {
                this.gridData = { data: currentValue.data.results, total: currentValue.data.inlineCount };
            } else {
                this.gridData = { data: [], total: 0 };
            }
            this.originalData = [...this.gridData.data];
            if (this.forceLocalSort || (!this.isLocalSortDisabled && this.gridData.total <= this.state.take)) {
                this.applyLocalSort();
            }
        }

        // if all the columns are hidden, don't show grid data in order not to show the columns by default.
        if (this.data && this.cols.length > 0 && this.cols.every((col) => !col.isVisible)) {
            this.gridData = { data: [], total: 0 };
        }

        this.gridSettings = {
            state: this.gridSettings?.state ?? this.state,
            columnsConfig: this.gridSettings?.columnsConfig ?? this.cols
        };
        if (this.autoFitColumns) this.fitColumns();
    }

    public onSortChanged(sort: SortDescriptor[]): void {
        this.saveAndLoadButtonsVisible = true;
        this.sort = sort;
        if (!this.forceLocalSort && (this.gridData.total > this.state.take || this.isLocalSortDisabled)) {
            this.queryData();
            this.sortChange.emit(sort);
        } else {
            this.state = { ...this.state, sort };
            this.applyLocalSort();
        }
    }

    onSaveGridSettings() {
        const gridSettings = {
            state: this.state,
            columnsConfig: this.columns
                .toArray()
                .filter((col: ColumnComponent) => col.field) // do not save columns without field(in case of selectable column)
                .map(
                    (item: ColumnComponent) =>
                        <ColumnSettings>{
                            field: item.field,
                            width: item.width,
                            title: item.title,
                            orderIndex: item.orderIndex,
                            isHidden: item.hidden,
                            style: item.style,
                            isSortable: item.sortable,
                            isVisible: true, // if the column was not visible, it would not be in the list
                            // we have to map to other properties that are not in ColumnBase interface from kendo
                            type: this.cols.find((x) => x.field === item.field)?.type,
                            isLink: this.cols.find((x) => x.field === item.field)?.isLink,
                            class: this.cols.find((x) => x.field === item.field)?.class,
                            icon: this.cols.find((x) => x.field === item.field)?.icon,
                            iconClass: this.cols.find((x) => x.field === item.field)?.iconClass,
                            codelistName: this.cols.find((x) => x.field === item.field)?.codelistName,
                            codelistTooltip: this.cols.find((x) => x.field === item.field)?.codelistTooltip,
                            codelistPill: this.cols.find((x) => x.field === item.field)?.codelistPill,
                            indicator: this.cols.find((x) => x.field === item.field)?.indicator,
                            customRoute: this.cols.find((x) => x.field === item.field)?.customRoute,
                            columnHeaderTooltip: this.cols.find((x) => x.field === item.field)?.columnHeaderTooltip,
                            maxLength: this.cols.find((x) => x.field === item.field)?.maxLength,
                            click: this.cols.find((x) => x.field === item.field)?.click
                        }
                )
        };
        this.saveGridSettings.emit({ value: gridSettings, key: this.gridSettingKey });
        this.gridSettingsExist = true;
        this.isGridCompressed = this.gridSettings?.columnsConfig?.some((x) => x.style?.['padding-block']);
        this.saveAndLoadButtonsVisible = false;
        this._savedStateExists = true;
    }

    public loadGridSettings(): void {
        this.commonService.settingsService
            .getGridSettings()
            .pipe(
                takeUntil(this.destroy$),
                tapSuccessResult((res) => {
                    if (!res) return;
                    const gridSetting = res.find((x) => x.key === this.commonService.getSanitizedUrl());
                    if (!gridSetting) return;
                    this.gridSettingKey = gridSetting.key;
                    this.gridSettings = this.mapGridSettings(JSON.parse(gridSetting.value));
                    this.isGridCompressed = this.gridSettings?.columnsConfig?.some((x) => x.style?.['padding-block']);
                    this.gridSettingsExist = true;
                    this.saveAndLoadButtonsVisible = false;
                    this.queryData();
                })
            )
            .subscribe();
    }

    public clearGridSettings(): void {
        this.gridSettings.columnsConfig = null;
        this.gridSettingsExist = false;
        this.saveAndLoadButtonsVisible = false;
        if (this.isGridCompressed) {
            this.cols.forEach((col) => (col.style = {}));
            this.isGridCompressed = false;
        }
        if (this.hasContentProjection && this.originalColumnState.size > 0) {
            const contentCols = this.columns.toArray() as ColumnComponent[];
            contentCols.forEach((col) => {
                const orig = this.originalColumnState.get(col.field);
                if (orig) {
                    col.width = orig.width;
                    col.orderIndex = orig.orderIndex;
                    col.hidden = orig.hidden;
                }
            });
            const restored = [...contentCols].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
            this.updateColumns(restored);
        }
        this.queryData();
        this.deleteGridSettings.emit();
        this._savedStateExists = false;
    }

    public onColumnChange(): void {
        this.saveAndLoadButtonsVisible = true;
    }

    public showTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;
        if (typeof element.className !== 'string') return;
        if (Array.from(element.children).find((child) => !child.classList.contains('k-checkbox'))) return;
        if (
            (element.nodeName === 'TD' ||
                element.className?.includes('k-column-title') ||
                (element.closest('td') && element.className?.includes('text-link'))) &&
            element.offsetWidth < element.scrollWidth
        ) {
            this.tooltipDir.toggle(element);
        } else {
            this.tooltipDir.hide();
        }
    }

    applyLocalSort() {
        const sort = this.sort[0];
        if (!sort?.dir) {
            this.gridData.data = [...this.originalData];
        } else if (this.customSort && sort.field === this.customSort.field) {
            const sorted = this.gridData.data.sort(this.customSort.sortFn);
            this.gridData.data = sort.dir === 'desc' ? sorted.reverse() : sorted;
        } else {
            this.gridData.data = orderBy(this.gridData.data, this.sort);
        }
    }

    onPageChange(event: PageChangeEvent) {
        this.saveAndLoadButtonsVisible = true;
        this.pageChange.emit(event);
        this.queryData();
    }

    // On detail expanded event close all other rows
    onDetailExpanded(event: DetailExpandEvent) {
        this.gridData.data.forEach((row, index) => {
            if (index !== event.index) this.grid.collapseRow(index);
        });
    }

    addColumn(column: ColumnBase, index?: number) {
        const columns = this.columns.toArray();
        if (index === undefined) columns.push(column);
        else columns.splice(index, 0, column);

        this.columns.reset(columns);
    }

    updateColumns(columns: ColumnBase[] = this.columns?.toArray() || []) {
        this.grid.columns.reset(columns);
        this.changeDetectorRef.detectChanges();
    }

    onDataStateChanged(state: DataStateChangeEvent) {
        if (state) this.state = state;

        if (this.applyLocalPagination) {
            let dataArray: any[] = [];

            if (this.data?.data?.results) dataArray = this.data.data.results;
            else dataArray = Array.isArray(this.data) ? this.data : [this.data];

            this.processGridData(dataArray, state);
        }

        if (this.autoFitColumns) this.fitColumns();
    }

    toggleCompression() {
        if (this.gridSettingsExist) {
            this.saveAndLoadButtonsVisible = this.isGridCompressed;
            this.isGridCompressed = !this.isGridCompressed;
        } else {
            this.isGridCompressed = !this.isGridCompressed;
            this.saveAndLoadButtonsVisible = this.isGridCompressed;
        }
        this.gridSettings.columnsConfig.forEach(
            (col) => (col.style = this.isGridCompressed ? { 'padding-block': this.compressedGridValue } : {})
        );
    }

    mapGridSettings(gridSettings: GridSettings): GridSettings {
        this.state = gridSettings.state;

        return {
            state: this.state,
            columnsConfig: gridSettings.columnsConfig.sort((a: any, b: any) => a.orderIndex - b.orderIndex)
        };
    }

    calculateGridHeight(): void {
        if (!this.resizeToWindow) return;
        const gridHeader = document.getElementById('content-header') as HTMLElement;
        if (gridHeader && !this.gridHeight) {
            this.gridHeight = window.innerHeight - (gridHeader.offsetHeight + 25);
        }
    }

    private getSavedState(): void {
        this.commonService.settingsService
            .getGridSettings()
            .pipe(
                takeUntil(this.destroy$),
                tapSuccessResult((res) => {
                    this._savedStateExists = res.some((x) => x.key === this.commonService.getSanitizedUrl());
                })
            )
            .subscribe();
    }

    private fitColumns(): void {
        this.ngZone.onStable
            .asObservable()
            .pipe(take(1))
            .subscribe(() => setTimeout(() => this.expandLastColumnToFit(), 0));
    }

    private changeSelection() {
        this.selectionKey =
            this.selectBy === 'row'
                ? (context: RowArgs) => context.dataItem
                : (context: RowArgs) => context.dataItem[this.selectBy];
    }

    private processGridData(gridData: any[], state: State) {
        this.gridData = {
            data: process(gridData, state).data,
            total: gridData.length
        };
    }

    private expandLastColumnToFit() {
        const columns = this.grid.columnList.toArray();
        this.grid.autoFitColumns();
        const totalWidthWithoutHidden = columns.reduce((sum, column) => (column.hidden ? sum : sum + column.width), 0);
        const availableWidth = this.grid.wrapper.nativeElement.offsetWidth - 17;
        const widthDelta = availableWidth - totalWidthWithoutHidden;
        if (widthDelta > 0) {
            let i = 1;
            let column = columns.at(-i);
            while (column.hidden) {
                i++;
                column = columns.at(-i);
            }
            column.width += widthDelta;
        }
    }
}
